import styled from '@emotion/styled';
import { AssignmentOutlined as LimitsIcon } from '@mui/icons-material';
import { Tooltip } from '@mui/material';
import cx from 'classnames';
import { filter, map } from 'lodash';
import React, { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  BankLabel,
  CopyText,
  PaymentTypeLabel,
  QrCodeWithDialog,
} from 'components';
import { useCurrencies } from 'hooks';
import { Requisites, User } from 'types';
import { formatUtils } from 'utils';

type Props = {
  id?: string;
  className?: string;
  compact?: boolean;
  cardInfo?: string;
  phone?: string;
  paymentTypeId?: string;
  bankId?: string;
  cardholder?: string;
  fiatCurrencyId?: string;
  canCopy?: boolean;
  swiftBic?: string;
  bic?: string;
  email?: string;
  idCard?: string;
  beneficiaryName?: string;
  accountNumber?: string;
  name?: string;
  expirationDate?: string;
  taxId?: string;
  accountLastDigits?: string;
  accountNumberQrCodeGenerated?: boolean;
  requisite?: Requisites;
  bankCode?: string;
  paymentTypeCode?: string;
  paymentLink?: string;
  showLimitsInfoButton?: boolean;
  showId?: boolean;
  showName?: boolean;
  showTrader?: boolean;
  user?: Partial<User>;
  traderId?: string;
  onLimitsInfoButtonClick?: () => void;
};

export const StyledBankLabel = styled(BankLabel)`
  font-weight: 500;
`;

export const RequisitesInfo: React.FC<Props> = ({
  className,
  compact,
  canCopy = true,
  requisite,
  bankCode,
  paymentTypeCode,
  showLimitsInfoButton,
  onLimitsInfoButtonClick,
  showName = true,
  showId,
  showTrader = false,
  ...rest
}) => {
  const { t } = useTranslation();

  const { getFiatCurrencyCode } = useCurrencies();
  const {
    id,
    cardInfo,
    phone,
    paymentTypeId,
    bankId,
    cardholder,
    fiatCurrencyId,
    swiftBic,
    taxId,
    bic,
    email,
    idCard,
    beneficiaryName,
    accountNumber,
    name,
    expirationDate,
    accountLastDigits,
    paymentLink,
    accountNumberQrCodeGenerated,
    user,
    traderId,
  } = useMemo(() => requisite || rest, [requisite, rest]);

  const requisites = useMemo(
    () => (
      <Fragment>
        {cardInfo && (
          <CopyText
            disabled={!canCopy}
            text={formatUtils.formatCardInfo(cardInfo)}
          />
        )}
        {expirationDate && (
          <CopyText
            disabled={!canCopy}
            text={expirationDate}
            iconColor="disabled"
          />
        )}
        {phone && <CopyText disabled={!canCopy} text={phone} />}
        {swiftBic && (
          <CopyText
            disabled={!canCopy}
            text={`${t(
              'features.form_builder.fields.swift_bic.label',
            )}: ${swiftBic}`}
            iconColor="disabled"
          />
        )}
        {bic && (
          <CopyText
            disabled={!canCopy}
            text={`${t('features.form_builder.fields.bic.label')}: ${bic}`}
            iconColor="disabled"
          />
        )}
        {taxId && (
          <CopyText
            disabled={!canCopy}
            text={`${t('features.form_builder.fields.tax_id.label')}: ${taxId}`}
            iconColor="disabled"
          />
        )}
        {email && (
          <CopyText disabled={!canCopy} text={email} iconColor="disabled" />
        )}
        {idCard && (
          <CopyText disabled={!canCopy} text={idCard} iconColor="disabled" />
        )}
        {beneficiaryName && (
          <CopyText
            disabled={!canCopy}
            text={beneficiaryName}
            iconColor="disabled"
          />
        )}
        {accountNumber &&
          (!accountNumberQrCodeGenerated ||
            formatUtils.isValidAccountNumberFormat(accountNumber)) && (
            <CopyText
              disabled={!canCopy}
              text={accountNumber}
              iconColor="disabled"
            />
          )}
        {accountNumber && accountNumberQrCodeGenerated && (
          <QrCodeWithDialog data={accountNumber} sx={{ ml: 1 }} />
        )}
        {paymentLink && (
          <div className="tw-break-all tw-min-w-[200px]">
            <CopyText
              disabled={!canCopy}
              text={paymentLink}
              label={formatUtils.formatPaymentLink(paymentLink)}
            />
          </div>
        )}
      </Fragment>
    ),
    [
      t,
      cardInfo,
      canCopy,
      phone,
      swiftBic,
      bic,
      email,
      idCard,
      beneficiaryName,
      accountNumber,
      expirationDate,
      taxId,
      paymentLink,
      accountNumberQrCodeGenerated,
    ],
  );

  const compactMainProperties = useMemo(
    () =>
      filter([
        showName ? name : null,
        fiatCurrencyId ? getFiatCurrencyCode(fiatCurrencyId) : null,
        paymentTypeId || paymentTypeCode ? (
          <PaymentTypeLabel
            paymentTypeId={paymentTypeId}
            paymentTypeCode={paymentTypeCode}
          />
        ) : null,
        bankId || bankCode ? (
          <BankLabel bankId={bankId} bankCode={bankCode} />
        ) : null,
        cardholder,
      ]),
    [
      showName,
      name,
      fiatCurrencyId,
      getFiatCurrencyCode,
      paymentTypeId,
      paymentTypeCode,
      bankId,
      bankCode,
      cardholder,
    ],
  );

  if (compact) {
    return (
      <div className={className}>
        {map(compactMainProperties, (value, index) => (
          <span className="tw-truncate" key={index}>
            {index !== 0 && ' | '}
            {value}
          </span>
        ))}

        <div className="tw-flex tw-flex-col">{requisites}</div>
        {showTrader && (
          <div className="tw-flex tw-flex-col">{user?.name || traderId}</div>
        )}
      </div>
    );
  }

  return (
    <div className={cx(className, 'tw-flex tw-flex-col')}>
      {name && showName && (
        <span className="tw-text-sm tw-font-semibold tw-inline-block">
          {name}
        </span>
      )}
      {id && showId && <CopyText text={id} />}
      {getFiatCurrencyCode(fiatCurrencyId)}
      {(paymentTypeId || paymentTypeCode) && (
        <PaymentTypeLabel
          paymentTypeId={paymentTypeId}
          paymentTypeCode={paymentTypeCode}
        />
      )}
      {showTrader && (
        <div className="tw-flex tw-flex-col">{user?.name || traderId}</div>
      )}
      <div className="tw-flex tw-items-center">
        {(bankId || bankCode) && (
          <StyledBankLabel bankId={bankId} bankCode={bankCode} />
        )}
        {showLimitsInfoButton && (
          <Tooltip
            title={t(
              'features.common_limits.requisite_limits.show_limits.show_current_limits',
            )}
          >
            <LimitsIcon
              sx={{ ml: 1 }}
              fontSize="small"
              color="primary"
              className="tw-cursor-pointer"
              onClick={onLimitsInfoButtonClick}
            />
          </Tooltip>
        )}
      </div>
      {requisites}
      {cardholder && <div>{cardholder}</div>}
      {accountLastDigits && <div>{accountLastDigits}</div>}
    </div>
  );
};
